import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, Link as LinkIcon, Stethoscope, Car } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import { consultingAPI } from '../../api';
import vehiclesData from '../../data/vehicles.json';

const VEHICLE_CATEGORIES = [
  'Voiture', 'Moto', 'Camion / Poids lourd', 'Tracteur agricole',
  'Bateau', 'Jet-ski', 'Engin de chantier', 'Autre'
];
const FUEL_TYPES = ['Diesel', 'Essence', 'Hybride', 'E85 Flexfuel', 'GPL', 'Autre'];
const GEARBOX_TYPES = ['Manuelle', 'Automatique', 'DSG / DCT', 'CVT', 'Autre'];
const TRANSMISSION_TYPES = ['2 roues motrices', '4 roues motrices (4x4)'];

const ConsultingForm = ({ user }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState('');
  const [availableModels, setAvailableModels] = useState([]);

  const [form, setForm] = useState({
    category: 'Voiture',
    brand: '',
    model: '',
    year: '',
    vin: '',
    licensePlate: '',
    engine: '',
    engineCode: '',
    power: '',
    fuel: '',
    gearbox: '',
    transmission: '',
    mileage: '',
    problem: '',
    diagnosticFile: null,
    diagnosticLink: ''
  });

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    setSelectedBrand(brandId);
    const brand = vehiclesData.brands.find(b => b.id === brandId);
    if (brand) {
      setAvailableModels(brand.models);
      setForm({ ...form, brand: brand.name, model: '' });
    } else {
      setAvailableModels([]);
      setForm({ ...form, brand: '', model: '' });
    }
  };

  const filteredBrands = vehiclesData.brands.filter(b => b.id !== 'autre');
  const filteredModels = availableModels.filter(m => m.toLowerCase() !== 'autre');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm({ ...form, diagnosticFile: file });
    toast({ title: "Fichier sélectionné", description: `${file.name} prêt à être uploadé` });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.problem.trim()) {
      toast({ title: "Erreur", description: "Veuillez décrire le problème rencontré", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const consulting = await consultingAPI.createConsulting({
        vehicle_info: {
          category: form.category,
          brand: form.brand,
          model: form.model,
          year: form.year,
          vin: form.vin,
          license_plate: form.licensePlate,
          engine: form.engine,
          engine_code: form.engineCode,
          power: form.power,
          fuel: form.fuel,
          gearbox: form.gearbox,
          transmission: form.transmission,
          mileage: form.mileage
        },
        problem: form.problem,
        diagnostic_link: form.diagnosticLink || null
      });

      if (form.diagnosticFile) {
        await consultingAPI.uploadFile(consulting.consulting_id, form.diagnosticFile);
      }

      toast({ title: "Demande créée !", description: `Consulting ${consulting.consulting_id} créé avec succès` });

      const paymentSession = await consultingAPI.createCheckoutSession(consulting.consulting_id);
      window.location.href = paymentSession.checkout_url;
    } catch (error) {
      console.error('Consulting creation error:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer la demande",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 bg-[#111111] border border-[#A6A6A6]/30 text-white rounded-md focus:border-[#C00000] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Consulting Diagnostic</span>
            </h1>
            <p className="text-[#A6A6A6]">Obtenez de l'aide pour diagnostiquer un problème véhicule</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="consulting-form">

            {/* SECTION 1 — VÉHICULE */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="h-5 w-5 text-[#C00000]" /> Informations du véhicule
                </CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Renseignez avec précision les caractéristiques du véhicule
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-[#A6A6A6]">Catégorie de véhicule *</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputCls}
                    data-testid="consulting-category-select"
                    required
                  >
                    {VEHICLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand" className="text-[#A6A6A6]">Marque *</Label>
                  <select
                    id="brand"
                    value={selectedBrand}
                    onChange={handleBrandChange}
                    className={inputCls}
                    data-testid="consulting-brand-select"
                    required
                  >
                    <option value="">Sélectionnez une marque</option>
                    {filteredBrands.map((brand) => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model" className="text-[#A6A6A6]">Modèle *</Label>
                  <select
                    id="model"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className={inputCls}
                    data-testid="consulting-model-select"
                    required
                    disabled={!selectedBrand}
                  >
                    <option value="">
                      {selectedBrand ? "Sélectionnez un modèle" : "Sélectionnez d'abord une marque"}
                    </option>
                    {filteredModels.map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Année *</Label>
                    <Input
                      value={form.year}
                      onChange={(e) => setForm({ ...form, year: e.target.value })}
                      placeholder="2018"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Kilométrage</Label>
                    <Input
                      value={form.mileage}
                      onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                      placeholder="120 000 km"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Numéro de chassis (VIN)</Label>
                    <Input
                      value={form.vin}
                      onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                      placeholder="VF1XXXXXXXXXXXXXX"
                      maxLength={17}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000] font-mono uppercase"
                      data-testid="consulting-vin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Immatriculation</Label>
                    <Input
                      value={form.licensePlate}
                      onChange={(e) => setForm({ ...form, licensePlate: e.target.value.toUpperCase() })}
                      placeholder="AB-123-CD"
                      maxLength={15}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000] font-mono uppercase"
                      data-testid="consulting-license-plate-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Moteur</Label>
                    <Input
                      value={form.engine}
                      onChange={(e) => setForm({ ...form, engine: e.target.value })}
                      placeholder="2.0 TDI"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Code moteur</Label>
                    <Input
                      value={form.engineCode}
                      onChange={(e) => setForm({ ...form, engineCode: e.target.value })}
                      placeholder="CUNA"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Puissance (cv)</Label>
                    <Input
                      value={form.power}
                      onChange={(e) => setForm({ ...form, power: e.target.value })}
                      placeholder="150"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Carburant</Label>
                    <select
                      value={form.fuel}
                      onChange={(e) => setForm({ ...form, fuel: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">Choisir...</option>
                      {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Boîte de vitesses</Label>
                    <select
                      value={form.gearbox}
                      onChange={(e) => setForm({ ...form, gearbox: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">Choisir...</option>
                      {GEARBOX_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A6A6A6]">Transmission</Label>
                  <select
                    value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">Choisir...</option>
                    {TRANSMISSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 2 — PROBLÈME */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Stethoscope className="h-5 w-5 text-[#C00000]" /> Description du problème
                </CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Décrivez le problème rencontré et joignez un diagnostic si disponible
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="problem" className="text-[#A6A6A6]">Description du problème *</Label>
                  <textarea
                    id="problem"
                    rows="6"
                    value={form.problem}
                    onChange={(e) => setForm({ ...form, problem: e.target.value })}
                    placeholder="Décrivez en détail le problème rencontré : symptômes, codes défauts, comportement du véhicule..."
                    className={`${inputCls} resize-none`}
                    required
                  ></textarea>
                </div>

                <div className="border-t border-[#A6A6A6]/20 pt-5">
                  <h3 className="text-white font-semibold mb-4">Diagnostic / Log (optionnel)</h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="diagnosticFile" className="text-[#A6A6A6]">Fichier de diagnostic</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="diagnosticFile"
                          type="file"
                          onChange={handleFileUpload}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                          accept=".pdf,.txt,.log,.xml,.csv,.bin,.hex"
                        />
                        <Upload className="h-5 w-5 text-[#C00000]" />
                      </div>
                      <p className="text-xs text-[#A6A6A6]">Formats acceptés : .pdf, .txt, .log, .xml, .csv, .bin, .hex</p>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                      <span className="text-[#A6A6A6] text-sm">OU</span>
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="diagnosticLink" className="text-[#A6A6A6]">Lien vers le diagnostic</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="diagnosticLink"
                          type="url"
                          value={form.diagnosticLink}
                          onChange={(e) => setForm({ ...form, diagnosticLink: e.target.value })}
                          placeholder="https://..."
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                        />
                        <LinkIcon className="h-5 w-5 text-[#C00000]" />
                      </div>
                      <p className="text-xs text-[#A6A6A6]">Lien WeTransfer, Google Drive, Dropbox, etc.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-[#1a1a1a] border-[#C00000]/30">
              <CardContent className="pt-6">
                <div className="bg-[#C00000]/10 border border-[#C00000]/30 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Stethoscope className="h-5 w-5 text-[#C00000] mt-0.5" />
                    <div>
                      <p className="text-white font-semibold mb-1">Service de consulting diagnostic</p>
                      <p className="text-[#A6A6A6] text-sm">
                        Un de nos ingénieurs analysera votre problème et vous apportera des conseils personnalisés via chat.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="consulting-submit-btn"
                  className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  <Stethoscope className="h-5 w-5 mr-2" />
                  {submitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConsultingForm;
